// ================================
// Enums
// ================================

import { AbstractDocumentV1 } from '@shared/models/abstract-document-v1.model';

/** BOOKING = ยังไม่จ่าย, PAID/SCAN/COMPLETE = สำเร็จ, CANCEL/TIMEOUT = ล้มเหลว */
export enum ReserveStatus {
    BOOKING = 'BOOKING', // รอชำระ - หน้าหลักควรแสดง "รอดำเนินการ"
    PAID = 'PAID', // จ่ายแล้ว - แสดง badge สีเขียว
    SCAN = 'SCAN', // scan QR แล้วที่สนาม
    COMPLETE = 'COMPLETE', // เสร็จสิ้น (golf bag)
    CANCEL = 'CANCEL', // ยกเลิกโดย user
    TIMEOUT = 'TIMEOUT' // หมดเวลาจ่าย
}

/** กลุ่ม status ใช้แยก success/fail เร็ว ๆ */
export enum ReserveStatusType {
    BEGIN = 'BEGIN', // กำลังดำเนินการ
    SUCCESS = 'SUCCESS', // สำเร็จ
    FAIL = 'FAIL' // ล้มเหลว
}

/** GOLFDIGG = จองผ่านแอป/เว็บ, OTA = จองผ่าน partner */
export enum ReserveType {
    GOLFDIGG = 'GOLFDIGG',
    OTA = 'OTA'
}

/** NORMAL = จองปกติ, REQUEST = จองแบบ request */
export enum ReserveChannel {
    NORMAL = 'NORMAL',
    REQUEST = 'REQUEST'
}

/** ช่องทางจ่ายเงิน - ใช้แสดงชื่อ payment method */
export enum PaymentGateway {
    OMISE = 'OMISE',
    MPAY = 'MPAY',
    LINEPAY = 'LINEPAY'
}

/** ประเภท source ของ payment */
export enum PaymentSourceType {
    CARD = 'CARD',
    MB = 'MB',
    QR = 'QR',
    LINEPAY = 'LINEPAY',
    Alipay = 'Alipay',
    KakaoPay = 'KakaoPay',
    LINK = 'LINK'
}

/** platform ที่ user ใช้จอง */
export enum PlatformType {
    IOS = 'IOS',
    ANDROID = 'ANDROID',
    GOLFDIGG = 'GOLFDIGG',
    WEBSITE = 'WEBSITE',
    NEWWEBSITE = 'NEWWEBSITE',
    DIRECT = 'DIRECT',
    TPC = 'TPC',
    TPCMEMBER = 'TPCMEMBER',
    TPCGUEST = 'TPCGUEST'
}

/** ประเภทส่วนลด */
export enum DiscountType {
    Coupon = 'Coupon',
    CreditDiscount = 'CreditDiscount',
    Voucher = 'Voucher',
    AIS = 'AIS',
    SUMMARY = 'SUMMARY'
}

export enum PackageOrderStatus {
    CREATE = 'CREATE',
    SUCCESS = 'SUCCESS',
    FAIL = 'FAIL'
}

export enum SettlementStatus {
    INITIAL = 'INITIAL',
    PENDING = 'PENDING',
    TRANSFERRED = 'TRANSFERRED',
    REFUND_PENDING = 'REFUND_PENDING',
    REFUND_COMPLETED = 'REFUND_COMPLETED',
    REFUND_REFUSED = 'REFUND_REFUSED',
    REFUND_CANCELLED = 'REFUND_CANCELLED',
    TRANSFER_CANCELLED = 'TRANSFER_CANCELLED'
}

export enum TaxProfileType {
    PERSONAL = 'PERSONAL',
    BUSINESS = 'BUSINESS'
}

export enum VoucherUsageStatus {
    RESERVE = 'RESERVE',
    COMMIT = 'COMMIT'
}

export enum CouponUsageStatus {
    RESERVE = 'RESERVE',
    COMMIT = 'COMMIT'
}

export enum CreditDiscountUsageStatus {
    RESERVE = 'RESERVE',
    COMMIT = 'COMMIT'
}

// ================================
// Sub types
// ================================

/** ข้อมูลการจ่ายเงิน gateway - แสดงในหน้าสรุป */
export interface ReservePaymentData {
    id?: string | null;
    /** ช่องทาง gateway จ่ายเงิน */
    gateway?: PaymentGateway | null;
    /** ประเภท source เช่น card / QR */
    sourceType?: PaymentSourceType | null;
    /** transaction id ของ payment gateway */
    transactionId?: string | null;
    /** สามารถ refund ได้ไหม - ใช้แสดง ปุ่ม refund */
    refundable?: boolean;
}

/** สรุปข้อมูล golf ในการจอง */
export interface Golf {
    /** slot id อ้างอิงกลับไป slot */
    slotId?: string | null;
    /** ราคา green fee ต่อคน */
    slotPrice?: number;
    /** ราคา cart ต่อคัน */
    cartPrice?: number;
    /** ราคา caddy ต่อคน */
    caddyPrice?: number;
    /** ราคา cart เดิมก่อน free */
    cartOriginalPrice?: number;
    /** ราคา caddy เดิมก่อน free */
    caddyOriginalPrice?: number;
    /** วันเวลา tee time - ใช้แสดงในหน้า booking detail */
    slotTime?: string | Date | null;
    slotTimeString?: string | null;
    /** จำนวนผู้เล่น */
    persons?: number;
    /** จำนวน cart */
    carts?: number;
    /** จำนวน caddy */
    caddies?: number;
    /** party booking */
    isParty?: boolean;
    /** ราคา green fee รวม = slotPrice × persons */
    slotsPrice?: number;
    /** ราคา cart รวม = cartPrice × carts */
    cartsPrice?: number;
    /** ราคา caddy รวม = caddyPrice × caddies */
    caddiesPrice?: number;
    /** NORMAL / HOTDEAL / SPECIAL - ใช้แสดง badge ราคา */
    priceCategory?: string | null;
}

/** รายการส่วนลดในการจอง - แสดงในหน้าสรุปราคา */
export interface Discount {
    id?: string | null;
    /** ประเภทส่วนลด Coupon/Voucher/AIS/Credit */
    type?: DiscountType | null;
    /** จำนวนเงินที่ลด */
    money?: number | null;
    /** คำอธิบาย เช่น "Voucher SUMMER20" */
    desc?: string | null;
}

/** ต้นทุนฝั่ง backend - FE ทั่วไปไม่แสดง แต่ backoffice ใช้ */
export interface Costs {
    /** settlement status ของสนาม - เอาไว้ตรวจสถานะโอนเงิน */
    status?: SettlementStatus | null;
    /** green fee ต้นทุนต่อคน */
    greenfee?: number;
    /** cart ต้นทุนต่อคัน */
    cart?: number;
    /** caddy ต้นทุนต่อคน */
    caddy?: number;
    /** ยอดที่ต้องโอนจริง (หลัง adjust) */
    adjustedAmount?: number | null;
    /** จำนวนที่โอนจริง */
    transferAmount?: number | null;
    /** วันที่โอนจริง */
    transferDate?: string | Date | null;
    note?: string | null;
}

/** Package add-on ที่จองคู่กัน เช่น ประกัน */
export interface ReservePackageOrderData {
    id?: string | null;
    packageId?: string | null;
    /** ชื่อ package */
    name?: string | null;
    /** จำนวน unit */
    unit?: number | null;
    pricePerUnit?: number | null;
    /** ราคารวม */
    amount?: number | null;
    status?: PackageOrderStatus | null;
}

/** Voucher ที่ใช้ใน booking */
export interface VoucherUsage {
    id?: string | null;
    voucherId?: string | null;
    /** ชื่อ voucher - ใช้แสดงในหน้าสรุปส่วนลด */
    voucherName?: string | null;
    userId?: string | null;
    status?: VoucherUsageStatus | null;
    discounts?: Discount[] | null;
}

/** Coupon ที่ใช้ใน booking */
export interface CouponUsage {
    id?: string | null;
    userId?: string | null;
    status?: CouponUsageStatus | null;
}

/** Credit discount ที่ใช้ใน booking */
export interface CreditDiscountUsage {
    id?: string | null;
    CreditDiscountId?: string | null;
    /** ชื่อ credit discount - แสดงในหน้าสรุปส่วนลด */
    CreditDiscountName?: string | null;
    userId?: string | null;
    status?: CreditDiscountUsageStatus | null;
}

/** ข้อมูลนักกอล์ฟแต่ละคน */
export interface Golfer {
    golferName?: string | null;
    golferMobile?: string | null;
}

/** ข้อมูล agency ถ้าจองผ่าน OTA/partner */
export interface AgencyInfo {
    memberId?: string | null;
    memberName?: string | null;
    jaNumber?: string | null;
    jaFile?: string | null;
}

/** Tax profile ที่ใช้ขอใบกำกับ */
export interface TaxProfile {
    taxProfileType?: TaxProfileType | null;
    id?: string | null;
    /** ชื่อผู้ขอใบกำกับ */
    name?: string | null;
    address?: string | null;
    email?: string | null;
}

/** ข้อมูล user ตอนจอง (snapshot) */
export interface UserBooking {
    id?: string | null;
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    mobile?: string | null;
}

// ================================
// Reserve (main)
// ================================

export interface Reserve extends AbstractDocumentV1{
    /** invoice ID ของ Golfdigg เช่น "GD-2026-001" - แสดงเป็นหมายเลขการจอง */
    gdInvoiceId?: string | null;
    code?: string | null;

    // --- status ---
    /**
     * สถานะการจอง - ใช้แสดง badge / flow UI
     * BOOKING = รอจ่าย | PAID/SCAN = สำเร็จ | CANCEL/TIMEOUT = ยกเลิก
     */
    status?: ReserveStatus | null;

    /** GOLFDIGG = ทั่วไป, OTA = จากพาร์ทเนอร์ */
    reserveType?: ReserveType | null;
    /** NORMAL = จองตรง, REQUEST = จองแบบ request */
    reserveChannel?: ReserveChannel | null;

    // --- golf detail ---
    /** ข้อมูลหลักของการเล่น: เวลา/ราคา/จำนวน - ใช้แสดงสรุปการจอง */
    golf?: Golf | null;

    // --- course info (snapshot ณ เวลาจอง) ---
    courseId?: string | null;
    /** ชื่อสนาม - แสดงใน booking list / detail */
    courseName?: string | null;
    courseSlugUrl?: string | null;
    /** จังหวัด/ที่ตั้ง */
    coursePlace?: string | null;
    courseTel?: string | null;
    /** รูปปก - thumbnail ใน booking list */
    courseImage?: string | null;

    // --- pricing ---
    /**
     * ราคารวมที่ user จ่ายจริง = green fee + cart + caddy - discount
     * ใช้แสดง "ยอดชำระ" ในหน้าสรุป
     */
    price?: number | null;
    /** GD point ที่ได้รับ */
    point?: number | null;
    /** รายการส่วนลดทั้งหมด - แสดงใน breakdown */
    discountList?: Discount[] | null;
    /** free cart (ไม่คิดค่า cart) */
    freeCart?: boolean | null;
    /** free caddy (ไม่คิดค่า caddy) */
    freeCaddy?: boolean | null;
    /** ข้อมูลต้นทุน - backoffice/admin เท่านั้น */
    costs?: Costs | null;

    // --- payment ---
    /** ช่องทาง payment ที่ใช้จ่าย เช่น OMISE / LINEPAY */
    payment?: string | null;
    sourceType?: string | null;
    paymentData?: ReservePaymentData | null;
    /** URL หน้าจ่ายเงิน - redirect ไปได้เลย */
    paymentURL?: string | null;
    /** deep link สำหรับ app */
    paymentURLApp?: string | null;

    // --- dates ---
    paidAt?: string | Date | null;
    /** วันที่ยกเลิก */
    revokeAt?: string | Date | null;
    refundAt?: string | Date | null;
    /** หมดเวลาจ่ายเงิน - แสดง countdown */
    expireAt?: string | Date | null;

    // --- user ---
    userId?: string | null;
    /** snapshot ข้อมูล user ณ ตอนจอง */
    user?: UserBooking | null;

    // --- golfer info ---
    /** ชื่อผู้เล่นหลัก (agent ระบุ) */
    golferName?: string | null;
    golferMobile?: string | null;
    golferId?: string | null;
    /** รายชื่อผู้เล่นทุกคน */
    golfers?: Golfer[] | null;

    // --- agency (OTA/partner) ---
    agencyId?: number | null;
    agencyInfo?: AgencyInfo | null;
    clientId?: string | null;

    // --- discounts / promotions ---
    voucherUsage?: VoucherUsage | null;
    couponUsage?: CouponUsage | null;
    creditDiscountUsage?: CreditDiscountUsage | null;

    // --- package add-on ---
    /** package เสริม เช่น ประกัน - แสดงใน booking detail ถ้ามี */
    packageOrder?: ReservePackageOrderData | null;

    // --- platform ---
    /** platform ที่ใช้จอง IOS/ANDROID/WEBSITE etc. */
    platformType?: PlatformType | null;

    // --- tax ---
    /** PERSONAL / BUSINESS */
    usedTaxProfileType?: TaxProfileType | null;
    /** ข้อมูลใบกำกับ - แสดงในหน้า invoice */
    taxProfile?: TaxProfile | null;

    // --- misc ---
    /** สาเหตุยกเลิก - แสดงใน cancel detail */
    cancellationCause?: string | null;
    note?: string | null;
    notes?: string[] | null;
    notice?: { en?: string; th?: string } | null;
    reviewed?: boolean;
}
